import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "form-group" }
const _hoisted_2 = {
  for: "phaseName",
  class: "required"
}
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = {
  key: 0,
  class: "error-message"
}
const _hoisted_5 = { class: "form-group" }
const _hoisted_6 = { for: "phaseDescription" }
const _hoisted_7 = ["placeholder"]
const _hoisted_8 = { class: "form-row" }
const _hoisted_9 = { class: "form-group" }
const _hoisted_10 = { for: "phaseStartDate" }
const _hoisted_11 = { class: "form-group" }
const _hoisted_12 = { for: "phaseEndDate" }
const _hoisted_13 = ["min"]
const _hoisted_14 = { class: "form-actions" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = {
  key: 0,
  class: "spinner"
}

import { ref as vueRef, computed as vueComputed, watch as vueWatch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import Modal from '@/components/base/Modal.vue'
import projectPhasesService from '@/services/projectPhases.service'

// Aliases to avoid TypeScript errors
interface Phase {
  id?: number
  projectId: number
  name: string
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PhaseFormModal',
  props: {
    show: { type: Boolean },
    phase: {},
    projectId: {}
  },
  emits: ["close", "saved"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const ref = vueRef
const computed = vueComputed
const watch = vueWatch

const props = __props

const emit = __emit

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// Form state
const form = ref<Phase>({
  projectId: props.projectId,
  name: '',
  description: '',
  plannedStartDate: '',
  plannedEndDate: ''
})

const errors = ref<Record<string, string>>({})
const submitting = ref(false)

// Computed
const isEditing = computed(() => !!props.phase?.id)

// Methods
const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.name || form.value.name.trim().length < 3) {
    errors.value.name = t('workspace.modal.errors.nameRequired')
    return false
  }
  
  if (form.value.plannedStartDate && form.value.plannedEndDate) {
    if (new Date(form.value.plannedEndDate) < new Date(form.value.plannedStartDate)) {
      errors.value.endDate = t('workspace.modal.errors.endDateBeforeStart')
      return false
    }
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return
  
  submitting.value = true
  
  try {
    const phaseData = {
      ...form.value,
      projectId: props.projectId
    }
    
    let savedPhaseId: number
    
    if (isEditing.value) {
      await projectPhasesService.updatePhase(
        props.projectId.toString(),
        props.phase!.id!.toString(),
        phaseData
      )
      savedPhaseId = props.phase!.id!
    } else {
      const response = await projectPhasesService.createPhase(
        props.projectId.toString(),
        phaseData
      )
      savedPhaseId = response.id
    }
    
    notifySuccess(t(isEditing.value ? 'workspace.success.phaseUpdated' : 'workspace.success.phaseCreated'))
    emit('saved')
    closeModal()
  } catch (error) {
    notifyError(t('workspace.errors.phaseSaveFailed'))
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  form.value = {
    projectId: props.projectId,
    name: '',
    description: '',
    plannedStartDate: '',
    plannedEndDate: ''
  }
  errors.value = {}
  emit('close')
}

// Watch for phase changes (editing mode)
watch(() => props.phase, (newPhase: Phase | null) => {
  if (newPhase) {
    form.value = {
      ...newPhase,
      projectId: props.projectId
    }
  } else {
    form.value = {
      projectId: props.projectId,
      name: '',
      description: '',
      plannedStartDate: '',
      plannedEndDate: ''
    }
  }
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: _ctx.show,
    title: _unref(isEditing) ? _ctx.$t('workspace.modal.editPhase') : _ctx.$t('workspace.modal.createPhase'),
    size: "xlarge",
    "close-on-backdrop-click": !_unref(submitting),
    loading: _unref(submitting),
    "no-padding": "",
    onClose: closeModal
  }, {
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "phase-form"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("label", _hoisted_2, _toDisplayString(_ctx.$t('workspace.modal.phaseName')), 1),
          _withDirectives(_createElementVNode("input", {
            id: "phaseName",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).name) = $event)),
            type: "text",
            placeholder: _ctx.$t('workspace.modal.phaseNamePlaceholder'),
            required: "",
            minlength: "3",
            maxlength: "100"
          }, null, 8, _hoisted_3), [
            [_vModelText, _unref(form).name]
          ]),
          (_unref(errors).name)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(errors).name), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('workspace.modal.description')), 1),
          _withDirectives(_createElementVNode("textarea", {
            id: "phaseDescription",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).description) = $event)),
            placeholder: _ctx.$t('workspace.modal.descriptionPlaceholder'),
            maxlength: "500",
            rows: "3"
          }, null, 8, _hoisted_7), [
            [_vModelText, _unref(form).description]
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("label", _hoisted_10, _toDisplayString(_ctx.$t('workspace.modal.startDate')), 1),
            _withDirectives(_createElementVNode("input", {
              id: "phaseStartDate",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).plannedStartDate) = $event)),
              type: "date"
            }, null, 512), [
              [_vModelText, _unref(form).plannedStartDate]
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("label", _hoisted_12, _toDisplayString(_ctx.$t('workspace.modal.endDate')), 1),
            _withDirectives(_createElementVNode("input", {
              id: "phaseEndDate",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).plannedEndDate) = $event)),
              type: "date",
              min: _unref(form).plannedStartDate
            }, null, 8, _hoisted_13), [
              [_vModelText, _unref(form).plannedEndDate]
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-secondary",
            onClick: closeModal
          }, _toDisplayString(_ctx.$t('common.cancel')), 1),
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-primary",
            disabled: _unref(submitting)
          }, [
            (_unref(submitting))
              ? (_openBlock(), _createElementBlock("span", _hoisted_16))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_unref(submitting) ? _ctx.$t('common.saving') : _ctx.$t('common.save')), 1)
          ], 8, _hoisted_15)
        ])
      ], 32)
    ]),
    _: 1
  }, 8, ["show", "title", "close-on-backdrop-click", "loading"]))
}
}

})